<script lang="ts">
  import { createEventDispatcher } from "svelte";

  const INPUT_LENGTH = 6;
  let inputRefs: HTMLInputElement[] = $state([]);
  let otpCode = new Array(INPUT_LENGTH).fill(""); // OTP values stored as an array of strings
  let isValid = false;

  const dispatch = createEventDispatcher();

  // Validate OTP inputs: ensure each is a number and all are filled
  const validateInputs = () => otpCode.every((digit) => /^\d$/.test(digit));

  // Update validity state and dispatch event with OTP data
  function updateValidity() {
    isValid = validateInputs();
    dispatch("change", { otpCode: otpCode.join(""), isValid });
  }

  // Handle changes to individual input fields
  function handleInput(index: number, event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    if (/^\d$/.test(value)) {
      otpCode[index] = value;

      // Move to the next input if available and valid input
      if (index < INPUT_LENGTH - 1) {
        inputRefs[index + 1]?.focus();
      }
    } else {
      // Clear the value if it's not a valid digit
      input.value = "";
    }

    updateValidity();
  }

  // Handle keydown events, specifically backspace behavior
  function handleKeydown(index: number, event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;

    // Handle backspace when the input is empty to move focus back
    if (event.key === "Backspace" && input.value === "" && index > 0) {
      otpCode[index] = ""; // Clear current position
      inputRefs[index - 1]?.focus(); // Move to previous input
    } else if (event.key === "Backspace" && input.value !== "") {
      // If there's a value, clear it but stay on the current input
      otpCode[index] = "";
    }

    updateValidity();
  }

  // Handle paste events, distribute pasted OTP code across inputs
  function handlePaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasteData = event.clipboardData?.getData("text").slice(0, INPUT_LENGTH);

    if (pasteData && /^\d+$/.test(pasteData)) {
      // Distribute pasted content across the inputs
      otpCode = pasteData.padEnd(INPUT_LENGTH, "").split("");

      // Fill inputs and update the DOM
      inputRefs.forEach((input, idx) => {
        input.value = otpCode[idx] || "";
      });

      // Focus the last filled input or the first empty one
      const focusIndex = Math.min(pasteData.length, INPUT_LENGTH - 1);
      inputRefs[focusIndex]?.focus();
    }

    updateValidity();
  }
</script>

{#each Array(INPUT_LENGTH) as _, i}
  <input
    type="text"
    inputmode="numeric"
    pattern="[0-9]*"
    bind:this={inputRefs[i]}
    maxlength="1"
    class="w-12 h-12 text-center text-xl font-semibold bg-primary-blue text-white rounded-lg border-2 border-primary-lightBlue focus:border-primary-fire focus:outline-none"
    oninput={(e) => handleInput(i, e)}
    onkeydown={(e) => handleKeydown(i, e)}
    onpaste={handlePaste}
  />
{/each}
